import { ICONS } from 'components/Icon';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm } from 'hooks/useConfirm';
import { pureComponent } from 'lib/pureComponent';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { File } from 'store/assets/types';
import { FilesContext } from 'types/types';
import { FileDisplay } from 'components/Display/FileDisplay';
import { FieldTypeIcon } from './FieldTypeIcon';

import { FileAddButtons } from './FileAddButtons';
import { IconButton } from 'components/IconButton';
import { pluralise } from 'lib/pluralise';

type Props = {
  value: string[];
  onChange: (value: string[]) => void;
  promptMessage: string;
  multiSelect?: boolean;
  files: Record<string, File>;
  context: FilesContext;
  onPreviewFile: (fileId: string) => void;
  onDownloadFile: (fileId: string) => void;
};

export const FileField: React.FC<Props> = pureComponent(
  ({
    context,
    multiSelect,
    onChange,
    promptMessage,
    value,
    files,
    onDownloadFile,
    onPreviewFile,
  }) => {
    const confirm = useConfirm();
    const handleAdd = React.useCallback(
      (fileIds: string[]) => {
        if (fileIds.length) {
          if (multiSelect) {
            onChange(
              value.concat(fileIds.filter((fileId) => !value.includes(fileId))),
            );
          } else {
            onChange([fileIds[0]!]);
          }
        }
      },
      [multiSelect, onChange, value],
    );

    const handleRemoveFile = (fileId: string) =>
      confirm({
        confirmText: 'Remove',
        onConfirm: () =>
          onChange(value.filter((currentFileId) => currentFileId !== fileId)),
        text: 'The file will remain available in RiskBase',
        title: 'Remove File',
        destructive: true,
      });

    const handleRemoveAll = () =>
      confirm({
        confirmText: 'Remove',
        onConfirm: () => onChange([]),
        text: 'Are you sure you’d like to remove all files? The files will remain available in RiskBase',
        title: pluralise(value.length, 'Remove {x} File', 'Remove {x} Files'),
        destructive: true,
      });

    return (
      <ViewWithSeparators style={styles.chips}>
        <View style={styles.buttonsContainer}>
          <FileAddButtons
            type="files"
            multiSelect={multiSelect}
            context={context}
            promptMessage={promptMessage}
            onChange={handleAdd}
            maximized
            glass={false}
          />
          <View style={{ flex: 1 }}></View>
          {!!value.length && (
            <IconButton
              icon={ICONS.TrashCanLight}
              glass={false}
              onPress={handleRemoveAll}
            />
          )}
          <FieldTypeIcon
            icon={ICONS.FileLight}
            style={{ marginRight: theme.padding / 2 }}
          />
        </View>
        <FileDisplay
          files={files}
          fileIds={value}
          nullOnNoFiles
          onPreview={onPreviewFile}
          onDownload={onDownloadFile}
          onRemove={handleRemoveFile}
        />
      </ViewWithSeparators>
    );
  },
);

const styles = StyleSheet.create({
  buttonsContainer: {
    backgroundColor: 'white',
    paddingLeft: theme.padding,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chip: {
    marginHorizontal: theme.padding,
  },
  chips: {
    backgroundColor: 'white',
  },
  container: {
    backgroundColor: 'white',
    paddingHorizontal: theme.padding,
    paddingVertical: theme.grid,
  },
});
